/**
 * Raw data export for the Forecast V3 workspace — the financial report's
 * "Raw data export" layout (Aperture budget template) fed from what the grid
 * is showing: one row per statement line, identifier columns on account rows,
 * one value column per month plus Total. A month column is "Actual" when the
 * grid is rolling actuals into it (Actuals + Forecast, closed month) and
 * "Forecast" / "Budget" otherwise — the export mirrors the toggle rather than
 * adding facets of its own.
 *
 * The Statistics band trails each location, as in the financial export, and
 * is rebuilt at stat-GL-account grain:
 *   - actual months read the GL stat actuals;
 *   - forecast months read the room nights the user keyed per stat account
 *     (revenue modal, stat-account mode) when the month has them, exactly as
 *     keyed;
 *   - otherwise the month's occupied rooms are spread across the Occupied
 *     Rooms stat accounts pro rata to their linked room-revenue accounts'
 *     on-screen revenue, so the stat leaves follow the revenue driver and tie
 *     back to the ORN row to the room night.
 */

import type { ForecastAccount } from '../types';
import { MONTHS } from '../types';

export interface RawExportDepartment {
  department: string;
  accounts: ForecastAccount[];
  monthlyTotals: Record<string, number>;
  total: number;
}

export interface RawExportStatAccount {
  statAccountId: number | null;
  chartOfAccountId: number;
  accountNumber: string;
  accountName: string;
  department: string | null;
  mappedType: string;
  operation: 'add' | 'subtract';
  revenueChartOfAccountId: number | null;
  /** Month number (1-12) → GL stat actual, operation-signed and absolute. */
  actuals: Record<string | number, number>;
  /** Month number (1-12) → room nights keyed in stat-account mode. */
  inputs: Record<string | number, number>;
  /** Month number (1-12) → ADR keyed in stat-account mode. */
  adrInputs?: Record<string | number, number>;
  /** Month number (1-12) → value keyed in the Other Statistics section. */
  values?: Record<string | number, number>;
  revenueDepartment?: string | null;
  revenueDepartmentId?: number | null;
}

export interface RawExportRoomStats {
  availableRooms: Record<string, number>;
  occupiedRooms: Record<string, number>;
  expanded?: Record<string, {
    forecast?: { availableRooms: number; occupiedRooms: number };
    actual?: { availableRooms: number; occupiedRooms: number };
    projected?: { availableRooms: number; occupiedRooms: number };
  }>;
}

export interface BuildForecastRawExportInput {
  locationName: string;
  year: number;
  /** Month keys ('jan'…) whose column shows actuals on screen. */
  actualMonthKeys: Set<string>;
  /** Column word for non-actual months — 'Forecast' or 'Budget'. */
  planLabel?: 'Forecast' | 'Budget';
  revenueDepartments: RawExportDepartment[];
  expenseDepartments: RawExportDepartment[];
  revenueTotals: Record<string, number>;
  expenseTotals: Record<string, number>;
  netIncome: Record<string, number>;
  roomStats?: RawExportRoomStats | null;
  statAccounts?: RawExportStatAccount[];
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type Cell = number | string;

function titleCase(s: string): string {
  return String(s || '')
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    .replace(/\bAnd\b/g, 'And');
}

const round2 = (v: number) => Math.round(v * 100) / 100;
const sumMonths = (cells: (number | null)[]): number | null => {
  const present = cells.filter((v): v is number => v !== null && Number.isFinite(v));
  return present.length ? present.reduce((a, b) => a + b, 0) : null;
};
const cellOf = (v: number | null | undefined): Cell => (v === null || v === undefined || !Number.isFinite(v) ? '' : v);

/**
 * Spread `total` whole units across `weights` proportionally (largest
 * remainder, so the parts sum to the total). Zero/absent weights share
 * nothing unless every weight is zero, in which case the split is even.
 */
export function allocateWhole(total: number, weights: number[]): number[] {
  const n = weights.length;
  if (n === 0) return [];
  const safeTotal = Math.max(0, Math.round(Number(total) || 0));
  const positive = weights.map((w) => (Number.isFinite(w) && w > 0 ? w : 0));
  const weightSum = positive.reduce((a, b) => a + b, 0);
  const basis = weightSum > 0 ? positive : weights.map(() => 1);
  const basisSum = weightSum > 0 ? weightSum : n;
  const exact = basis.map((w) => (safeTotal * w) / basisSum);
  const floors = exact.map((v) => Math.floor(v));
  let remainder = safeTotal - floors.reduce((a, b) => a + b, 0);
  const order = exact
    .map((v, i) => ({ i, frac: v - floors[i] }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of order) {
    if (remainder <= 0) break;
    floors[i] += 1;
    remainder -= 1;
  }
  return floors;
}

/** On-screen ORN / ARN for a month: rolled GL actuals on actual months when recorded, else the projection. */
function screenRoomStats(roomStats: RawExportRoomStats | null | undefined, monthKey: string, isActual: boolean) {
  if (!roomStats) return { occupied: null as number | null, available: null as number | null };
  const expanded = roomStats.expanded?.[monthKey];
  const actual = expanded?.actual;
  if (isActual && actual && actual.availableRooms > 0 && actual.occupiedRooms > 0) {
    return { occupied: actual.occupiedRooms, available: actual.availableRooms };
  }
  const occupied = expanded?.projected?.occupiedRooms ?? expanded?.forecast?.occupiedRooms ?? roomStats.occupiedRooms?.[monthKey] ?? null;
  const available = expanded?.projected?.availableRooms ?? expanded?.forecast?.availableRooms ?? roomStats.availableRooms?.[monthKey] ?? null;
  return { occupied: occupied ?? null, available: available ?? null };
}

export interface StatLeaf {
  account: RawExportStatAccount;
  months: (number | null)[];
  /** How each month's value was resolved — 'actual' (GL), 'keyed' (stat-account mode), 'prorata' (spread by revenue), or null when blank. */
  sources: (StatLeafSource | null)[];
}
export type StatLeafSource = 'actual' | 'keyed' | 'prorata';

export type StatAccountMonthsInput = Pick<BuildForecastRawExportInput, 'actualMonthKeys' | 'revenueDepartments' | 'roomStats' | 'statAccounts'>;

/**
 * Per-stat-account monthly values. Exported for tests; the builder below
 * turns these into rows.
 */
export function resolveStatAccountMonths(input: StatAccountMonthsInput): {
  occupiedLeaves: StatLeaf[];
  availableLeaves: StatLeaf[];
  otherMapped: Map<string, StatLeaf[]>;
  unmapped: StatLeaf[];
  occupied: (number | null)[];
  available: (number | null)[];
} {
  const { actualMonthKeys, roomStats, statAccounts = [] } = input;
  const revenueByAccount = new Map<number, ForecastAccount>();
  input.revenueDepartments.forEach((d) => d.accounts.forEach((a) => revenueByAccount.set(a.chartOfAccountId, a)));

  const occupied: (number | null)[] = [];
  const available: (number | null)[] = [];
  MONTHS.forEach((m) => {
    const s = screenRoomStats(roomStats, m, actualMonthKeys.has(m));
    occupied.push(s.occupied);
    available.push(s.available);
  });

  const byType = (t: string) => statAccounts.filter((a) => a.mappedType === t);
  const occAccounts = byType('Occupied Rooms');
  const availAccounts = byType('Available Rooms');

  const actualOf = (a: RawExportStatAccount, monthNumber: number): number | null => {
    const v = a.actuals?.[monthNumber] ?? a.actuals?.[String(monthNumber)];
    return v === undefined || v === null ? null : Number(v);
  };
  const inputOf = (a: RawExportStatAccount, monthNumber: number): number | null => {
    const v = a.inputs?.[monthNumber] ?? a.inputs?.[String(monthNumber)];
    return v === undefined || v === null ? null : Number(v);
  };

  const blankLeaf = (account: RawExportStatAccount): StatLeaf => ({ account, months: MONTHS.map(() => null), sources: MONTHS.map(() => null) });
  const set = (leaf: StatLeaf, idx: number, value: number | null, source: StatLeafSource) => {
    leaf.months[idx] = value;
    leaf.sources[idx] = value === null ? null : source;
  };
  const occupiedLeaves: StatLeaf[] = occAccounts.map(blankLeaf);
  MONTHS.forEach((monthKey, idx) => {
    const monthNumber = idx + 1;
    if (actualMonthKeys.has(monthKey)) {
      occupiedLeaves.forEach((leaf) => set(leaf, idx, actualOf(leaf.account, monthNumber), 'actual'));
      return;
    }
    // Stat-account mode writes every segment for the month — a keyed month
    // exports exactly as keyed. An all-zero key set is the modal opened in
    // stat mode with nothing entered, so it falls through to the spread.
    const keyed = occupiedLeaves.some((leaf) => (inputOf(leaf.account, monthNumber) ?? 0) > 0);
    if (keyed) {
      occupiedLeaves.forEach((leaf) => set(leaf, idx, inputOf(leaf.account, monthNumber) ?? 0, 'keyed'));
      return;
    }
    const orn = occupied[idx];
    if (orn === null || occupiedLeaves.length === 0) return;
    const weights = occupiedLeaves.map((leaf) => {
      const rev = leaf.account.revenueChartOfAccountId != null ? revenueByAccount.get(leaf.account.revenueChartOfAccountId) : null;
      const v = rev?.monthlyValues?.[monthKey as keyof ForecastAccount['monthlyValues']];
      return typeof v === 'number' && v > 0 ? v : 0;
    });
    const shares = allocateWhole(orn, weights);
    occupiedLeaves.forEach((leaf, i) => set(leaf, idx, shares[i], 'prorata'));
  });

  const availableLeaves: StatLeaf[] = availAccounts.map(blankLeaf);
  MONTHS.forEach((monthKey, idx) => {
    const monthNumber = idx + 1;
    if (actualMonthKeys.has(monthKey)) {
      availableLeaves.forEach((leaf) => set(leaf, idx, actualOf(leaf.account, monthNumber), 'actual'));
      return;
    }
    const arn = available[idx];
    if (arn === null || availableLeaves.length === 0) return;
    const shares = allocateWhole(arn, availableLeaves.map(() => 1));
    availableLeaves.forEach((leaf, i) => set(leaf, idx, shares[i], 'prorata'));
  });

  // Non-room-night stat accounts: GL actual on actual months, else whatever
  // was keyed in the workspace's Other Statistics section (blank when nothing).
  const keyedOf = (a: RawExportStatAccount, monthNumber: number): number | null => {
    const v = a.values?.[monthNumber] ?? a.values?.[String(monthNumber)];
    return v === undefined || v === null ? null : Number(v);
  };
  const actualsOnlyLeaf = (account: RawExportStatAccount): StatLeaf => {
    const leaf = blankLeaf(account);
    MONTHS.forEach((m, idx) => {
      if (actualMonthKeys.has(m)) set(leaf, idx, actualOf(account, idx + 1), 'actual');
      else set(leaf, idx, keyedOf(account, idx + 1), 'keyed');
    });
    return leaf;
  };
  const otherMapped = new Map<string, StatLeaf[]>();
  statAccounts
    .filter((a) => a.mappedType && a.mappedType !== 'Occupied Rooms' && a.mappedType !== 'Available Rooms')
    .forEach((account) => {
      const list = otherMapped.get(account.mappedType) || [];
      list.push(actualsOnlyLeaf(account));
      otherMapped.set(account.mappedType, list);
    });
  const unmapped: StatLeaf[] = statAccounts.filter((a) => !a.mappedType).map(actualsOnlyLeaf);

  return { occupiedLeaves, availableLeaves, otherMapped, unmapped, occupied, available };
}

const sortLeaves = (leaves: StatLeaf[]) =>
  [...leaves].sort(
    (a, b) =>
      a.account.accountNumber.localeCompare(b.account.accountNumber, undefined, { numeric: true }) ||
      a.account.accountName.localeCompare(b.account.accountName)
  );

/** Returns an array of arrays (header first) ready for a CSV writer. */
export function buildForecastRawExport(input: BuildForecastRawExportInput): Cell[][] {
  const { locationName, year, actualMonthKeys, planLabel = 'Forecast' } = input;
  const header: Cell[] = [
    'Location',
    'Account',
    'Lodgeic Account ID',
    'Account Number',
    'Department',
    ...MONTHS.map((m, idx) => `${MONTH_NAMES[idx]} ${year} ${actualMonthKeys.has(m) ? 'Actual' : planLabel}`),
    'Total',
  ];

  const rows: Cell[][] = [];
  const push = (label: string, months: (number | null)[], ids: { accountId?: number | null; accountNumber?: string; department?: string | null } = {}, total?: number | null) => {
    const t = total === undefined ? sumMonths(months) : total;
    rows.push([
      locationName,
      label,
      ids.accountId ?? '',
      ids.accountNumber || '',
      ids.department && ids.department !== '(No department)' ? titleCase(ids.department) : '',
      ...months.map(cellOf),
      cellOf(t),
    ]);
  };
  const monthsOf = (rec: Record<string, number>) => MONTHS.map((m) => round2(Number(rec?.[m] ?? 0)));
  const accountMonths = (a: ForecastAccount) => MONTHS.map((m) => round2(Number(a.monthlyValues?.[m] ?? 0)));

  push('Net Income', monthsOf(input.netIncome));
  push('Revenue', monthsOf(input.revenueTotals));
  input.revenueDepartments.forEach((dept) => {
    push(titleCase(dept.department), monthsOf(dept.monthlyTotals));
    dept.accounts.forEach((a) =>
      push(a.accountName, accountMonths(a), { accountId: a.chartOfAccountId, accountNumber: a.accountNumber, department: dept.department })
    );
  });
  push('Expense', monthsOf(input.expenseTotals));
  input.expenseDepartments.forEach((dept) => {
    push(titleCase(dept.department), monthsOf(dept.monthlyTotals));
    dept.accounts.forEach((a) =>
      push(a.accountName, accountMonths(a), { accountId: a.chartOfAccountId, accountNumber: a.accountNumber, department: dept.department })
    );
  });

  // Statistics band — trailing, as in the financial raw export.
  const stats = resolveStatAccountMonths(input);
  const hasStats = !!input.roomStats || (input.statAccounts?.length ?? 0) > 0;
  if (hasStats) {
    const occTotal = sumMonths(stats.occupied);
    const availTotal = sumMonths(stats.available);
    const occPct = MONTHS.map((_, i) => {
      const o = stats.occupied[i];
      const a = stats.available[i];
      return o === null || a === null || !(a > 0) ? null : (o / a) * 100;
    });
    const occPctTotal = occTotal === null || availTotal === null || !(availTotal > 0) ? null : (occTotal / availTotal) * 100;
    const hasPair = occPct.some((v) => v !== null);

    push(hasPair ? 'Statistics · Occ %' : 'Statistics', hasPair ? occPct : MONTHS.map(() => null), {}, hasPair ? occPctTotal : null);

    const leafIds = (leaf: StatLeaf) => ({
      accountId: leaf.account.chartOfAccountId,
      accountNumber: leaf.account.accountNumber,
      department: leaf.account.department,
    });
    push('Total Occupied Rooms', stats.occupied, {}, occTotal);
    sortLeaves(stats.occupiedLeaves).forEach((leaf) => push(leaf.account.accountName, leaf.months, leafIds(leaf)));
    push('Total Available Rooms', stats.available, {}, availTotal);
    sortLeaves(stats.availableLeaves).forEach((leaf) => push(leaf.account.accountName, leaf.months, leafIds(leaf)));
    if (hasPair) push('Occupancy %', occPct, {}, occPctTotal);

    Array.from(stats.otherMapped.keys()).sort((a, b) => a.localeCompare(b)).forEach((type) => {
      const leaves = sortLeaves(stats.otherMapped.get(type) || []);
      const totals = MONTHS.map((_, i) => sumMonths(leaves.map((l) => l.months[i])));
      push(`Total ${titleCase(type)}`, totals);
      leaves.forEach((leaf) => push(leaf.account.accountName, leaf.months, leafIds(leaf)));
    });
    if (stats.unmapped.length > 0) {
      push('Other Statistics', MONTHS.map(() => null), {}, null);
      sortLeaves(stats.unmapped).forEach((leaf) => push(leaf.account.accountName, leaf.months, leafIds(leaf)));
    }
  }

  return [header, ...rows];
}

export function rawExportToCsv(aoa: Cell[][]): string {
  const escape = (v: Cell) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return `\uFEFF${aoa.map((row) => row.map(escape).join(',')).join('\r\n')}`;
}
